package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.MarketingSection
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.layout.PageHero
import co.maytes.standupbot.dashboard.components.widgets.CardGrid
import co.maytes.standupbot.dashboard.components.widgets.MonoQuote
import co.maytes.standupbot.dashboard.components.widgets.RevealOnScroll
import co.maytes.standupbot.dashboard.components.widgets.StretchCard
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.marketing.Claims
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Page("/docs/anthropic-key")
@Composable
fun AnthropicKeyPage() {
    MarketingShell {
        PageHero(
            title = "Create your Anthropic key",
            subtitle = "StandupBot writes your standups with Claude, running on your own Anthropic " +
                "account. Allow ${Claims.KEY_SETUP_TIME}. One copy-paste, and you keep control of " +
                "the spend and the data.",
        )
        FactStrip()
        StepsSection()
        SpendGuardSection()
        ModelSection()
        SafetySection()
        TroubleshootingSection()
    }
}

private data class Fact(val value: String, val label: String)

private val facts = listOf(
    Fact("~5 min", "plus adding credit"),
    Fact("$5–15", "per month, 10-person team"),
    Fact("Yours", "your account, your terms"),
)

@Composable
private fun FactStrip() {
    MarketingSection(maxWidthPx = 880) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                    property("gap", "12px")
                },
        ) {
            facts.forEachIndexed { idx, fact ->
                RevealOnScroll(delayMs = 60 * idx) {
                    Box(
                        modifier = Modifier
                            .classNames("sb-surface")
                            .padding(Spacing.md)
                            .borderRadius(Radius.md)
                            .styleModifier {
                                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                                property("min-width", "180px")
                                property("flex", "1 1 180px")
                            },
                    ) {
                        Column {
                            SpanText(
                                text = fact.value,
                                modifier = Modifier
                                    .classNames("sb-text-primary")
                                    .fontSize(Typography.h2.size)
                                    .fontWeight(700)
                                    .styleModifier { property("letter-spacing", "-0.02em") },
                            )
                            SpanText(
                                text = fact.label,
                                modifier = Modifier
                                    .fontSize(Typography.bodySmall.size)
                                    .color(Palette.textTertiary)
                                    .margin(top = Spacing.xs),
                            )
                        }
                    }
                }
            }
        }
    }
}

private data class KeyStep(
    val index: Int,
    val title: String,
    val body: String,
    val literal: String? = null,
)

private val keySteps: List<KeyStep> = listOf(
    KeyStep(
        index = 1,
        title = "Open the Anthropic Console",
        body = "Go to console.anthropic.com and sign in, or create an account. This is Anthropic's " +
            "own site, you're setting up a direct relationship with them, not going through us.",
        literal = "console.anthropic.com",
    ),
    KeyStep(
        index = 2,
        title = "Add billing before you generate a key",
        body = "Under Billing, add a card and buy a small amount of credit. Keys created on an " +
            "account with no credit authenticate fine but fail on the first real request, which " +
            "is a confusing way to discover the problem. Start with $10, a 10-person team " +
            "typically spends $5–15 a month.",
    ),
    KeyStep(
        index = 3,
        title = "Create the key",
        body = "Go to API Keys and choose Create Key. Name it something you'll recognise later, " +
            "like standupbot, so it's obvious what to revoke if you ever need to.",
        literal = "API Keys → Create Key → name it \"standupbot\"",
    ),
    KeyStep(
        index = 4,
        title = "Copy it now, you only get one look",
        body = "Anthropic shows the key exactly once. Copy it straight into StandupBot rather " +
            "than parking it in a note or a chat message. If you lose it, no harm done: delete " +
            "that key and create another.",
        literal = "sk-ant-api03-…",
    ),
    KeyStep(
        index = 5,
        title = "Paste it into StandupBot",
        body = "In StandupBot open Settings → Integrations and find the Anthropic card. Paste the " +
            "key, pick a model and save, then use Test connection to confirm it works.",
    ),
)

@Composable
private fun StepsSection() {
    MarketingSection(maxWidthPx = 880) {
        Column(modifier = Modifier.fillMaxWidth()) {
            keySteps.forEachIndexed { idx, step ->
                RevealOnScroll(delayMs = 70 * idx) {
                    StepCard(step)
                }
            }
        }
    }
}

@Composable
private fun StepCard(step: KeyStep) {
    Box(
        modifier = Modifier
            .classNames("sb-surface")
            .fillMaxWidth()
            .padding(Spacing.lg)
            .borderRadius(Radius.md)
            .margin(bottom = Spacing.md)
            .styleModifier {
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
            },
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top,
        ) {
            StepBadge(step.index)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(left = Spacing.md),
            ) {
                SpanText(
                    text = step.title,
                    modifier = Modifier
                        .classNames("sb-text-primary")
                        .fontSize(Typography.h3.size)
                        .fontWeight(600)
                        .margin(bottom = Spacing.xs),
                )
                SpanText(
                    text = step.body,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.textSecondary)
                        .styleModifier { property("line-height", "1.65") },
                )
                step.literal?.let {
                    Box(modifier = Modifier.margin(top = Spacing.sm).fillMaxWidth()) {
                        MonoQuote(text = it)
                    }
                }
            }
        }
    }
}

@Composable
private fun StepBadge(n: Int) {
    Box(
        modifier = Modifier
            .backgroundColor(Palette.primary)
            .color(Palette.onPrimary)
            .borderRadius(Radius.sm)
            .styleModifier {
                property("width", "32px")
                property("height", "32px")
                property("min-width", "32px")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("font-weight", "600")
                property("font-size", "14px")
                property("box-shadow", "0 1px 2px rgba(10, 10, 18, 0.25)")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(text = n.toString())
    }
}

@Composable
private fun SpendGuardSection() {
    MarketingSection(maxWidthPx = 880) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(Spacing.lg)
                .borderRadius(Radius.md)
                .styleModifier {
                    property("border", "1px solid ${Palette.accent.cssRgb()}")
                    property("background", "color-mix(in srgb, ${Palette.accent.cssRgb()} 7%, transparent)")
                },
        ) {
            Column {
                SpanText(
                    text = "Set a spend limit while you're in there.",
                    modifier = Modifier
                        .classNames("sb-text-primary")
                        .fontSize(Typography.h2.size)
                        .fontWeight(700)
                        .margin(bottom = Spacing.sm)
                        .styleModifier {
                            property("letter-spacing", "-0.02em")
                            property("line-height", "1.2")
                            property("display", "block")
                        },
                )
                SpanText(
                    text = "Anthropic's console has a monthly spend limit and email alerts. Set them. " +
                        "It costs nothing, and it means a runaway month is capped by your own account " +
                        "rather than discovered on a statement. StandupBot also enforces its own " +
                        "monthly cost cap and will fall back to raw Linear activity rather than keep " +
                        "spending, but a limit at the source is the one that can't be argued with.",
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.textSecondary)
                        .styleModifier {
                            property("line-height", "1.7")
                            property("display", "block")
                        },
                )
            }
        }
    }
}

private data class ModelChoice(
    val name: String,
    val price: String,
    val body: String,
    val recommended: Boolean = false,
)

private val modelChoices = listOf(
    ModelChoice(
        name = "Claude Haiku 5.5",
        price = "$0.10 in / $0.50 out per million tokens",
        body = "The cheapest way to run. Good for short, factual standups where the Linear " +
            "activity already tells most of the story.",
    ),
    ModelChoice(
        name = "Claude Sonnet 5.5",
        price = "$2 in / $10 out per million tokens",
        body = "The default, and what we'd pick. Reads messy sprint activity well and writes " +
            "something a human would recognise as their own week.",
        recommended = true,
    ),
    ModelChoice(
        name = "Claude Opus 5.5",
        price = "$4 in / $20 out per million tokens",
        body = "The most capable, and roughly twice Sonnet's cost, and it always thinks before answering, so a standup takes longer. Worth trying for " +
            "accomplishment summaries at review time rather than for every daily standup.",
    ),
)

@Composable
private fun ModelSection() {
    MarketingSection(bordered = true, maxWidthPx = 1120) {
        SpanText(
            text = "You can change this any time.",
            modifier = Modifier
                .classNames("sb-text-primary")
                .fontSize(Typography.h1.size)
                .fontWeight(700)
                .margin(bottom = Spacing.md)
                .styleModifier {
                    property("letter-spacing", "-0.02em")
                    property("line-height", "1.15")
                    property("display", "block")
                },
        )
        SpanText(
            text = "Your key and model are stored per workspace, and model choice is not a plan tier. " +
                "The model you save is used when you regenerate a standup; scheduled runs currently " +
                "use StandupBot's default model. Start on Sonnet.",
            modifier = Modifier
                .fontSize(Typography.body.size)
                .color(Palette.textSecondary)
                .margin(bottom = Spacing.xl)
                .styleModifier {
                    property("line-height", "1.6")
                    property("display", "block")
                    property("max-width", "620px")
                },
        )
        CardGrid {
            modelChoices.forEachIndexed { idx, model ->
                RevealOnScroll(delayMs = 90 * idx) {
                    ModelCard(model)
                }
            }
        }
    }
}

@Composable
private fun ModelCard(model: ModelChoice) {
    StretchCard {
        if (model.recommended) {
            Box(
                modifier = Modifier
                    .backgroundColor(Palette.accent)
                    .color(Palette.onPrimary)
                    .borderRadius(Radius.sm)
                    .padding(left = 8.px, right = 8.px, top = 3.px, bottom = 3.px)
                    .margin(bottom = Spacing.sm)
                    .styleModifier {
                        property("display", "inline-block")
                        property("font-size", "11px")
                        property("font-weight", "600")
                        property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase")
                    },
            ) {
                SpanText("Recommended")
            }
        }
        SpanText(
            text = model.name,
            modifier = Modifier
                .classNames("sb-text-primary")
                .fontSize(Typography.h3.size)
                .fontWeight(600)
                .margin(bottom = Spacing.xs),
        )
        SpanText(
            text = model.price,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.accent)
                .fontWeight(500)
                .margin(bottom = Spacing.sm)
                .styleModifier { property("display", "block") },
        )
        SpanText(
            text = model.body,
            modifier = Modifier
                .fontSize(Typography.body.size)
                .color(Palette.textSecondary)
                .styleModifier {
                    property("line-height", "1.6")
                    property("flex", "1 1 auto")
                },
        )
    }
}

private data class SafetyNote(val title: String, val body: String)

private val safetyNotes = listOf(
    SafetyNote(
        title = "Encrypted the moment you save it",
        body = "Your key is sealed with AES-GCM envelope encryption under a per-workspace keyring " +
            "before it touches the database. It is never written to logs and never returned by " +
            "the API, the dashboard shows a masked preview only.",
    ),
    SafetyNote(
        title = "Revocable without asking us",
        body = "Delete the key in Anthropic's console and it stops working immediately, whatever " +
            "we do. That's the point of BYOK: the off switch is yours, not ours.",
    ),
    SafetyNote(
        title = "Rotate whenever you like",
        body = "Paste a new key over the old one in Settings → Integrations. The next standup " +
            "picks it up; nothing else needs changing.",
    ),
    SafetyNote(
        title = "Your account, your terms",
        body = "Issue titles, PR descriptions and commit activity are processed under your " +
            "Anthropic agreement and retention policy, not ours. Tools that send your work to " +
            "their own vendor account cannot offer that.",
    ),
)

@Composable
private fun SafetySection() {
    MarketingSection(maxWidthPx = 1120) {
        SpanText(
            text = "You're handing over a credential. Here's exactly where it goes.",
            modifier = Modifier
                .classNames("sb-text-primary")
                .fontSize(Typography.h1.size)
                .fontWeight(700)
                .margin(bottom = Spacing.xl)
                .styleModifier {
                    property("letter-spacing", "-0.02em")
                    property("line-height", "1.15")
                    property("display", "block")
                    property("max-width", "700px")
                },
        )
        CardGrid {
            safetyNotes.forEachIndexed { idx, note ->
                RevealOnScroll(delayMs = 80 * idx) {
                    StretchCard {
                        SpanText(
                            text = note.title,
                            modifier = Modifier
                                .classNames("sb-text-primary")
                                .fontSize(Typography.h3.size)
                                .fontWeight(600)
                                .margin(bottom = Spacing.xs),
                        )
                        SpanText(
                            text = note.body,
                            modifier = Modifier
                                .fontSize(Typography.body.size)
                                .color(Palette.textSecondary)
                                .styleModifier {
                                    property("line-height", "1.6")
                                    property("flex", "1 1 auto")
                                },
                        )
                    }
                }
            }
        }
    }
}

private data class Trouble(val question: String, val answer: String)

private val troubles = listOf(
    Trouble(
        question = "It says the key is invalid",
        answer = "Check for a trailing space, and check you copied the whole thing, real keys " +
            "start with sk-ant-. If it still fails, delete it in the console and make a new one; " +
            "a half-copied key is far more common than a broken one.",
    ),
    Trouble(
        question = "It saved, but standups come out as plain activity lists",
        answer = "That's the non-AI fallback doing its job. Usually it means the Anthropic account " +
            "is out of credit, or the workspace hit its monthly cost cap. Both are visible on the " +
            "run detail page in the dashboard.",
    ),
    Trouble(
        question = "Can the whole team share one key?",
        answer = "Yes, one key per workspace is exactly the intent. Individual teammates never " +
            "need Anthropic accounts, and most never know the key exists.",
    ),
    Trouble(
        question = "What if I'd rather not do this at all?",
        answer = "Then StandupBot isn't the right fit, and we'd rather say so plainly. Running on " +
            "your key is what lets us charge one flat price regardless of headcount instead of " +
            "billing you per seat.",
    ),
)

@Composable
private fun TroubleshootingSection() {
    MarketingSection(bordered = true, maxWidthPx = 760) {
        Column(modifier = Modifier.fillMaxWidth()) {
            troubles.forEach { item ->
                Column(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.lg)) {
                    SpanText(
                        text = item.question,
                        modifier = Modifier
                            .classNames("sb-text-primary")
                            .fontSize(Typography.h3.size)
                            .fontWeight(600)
                            .margin(bottom = Spacing.xs),
                    )
                    SpanText(
                        text = item.answer,
                        modifier = Modifier
                            .fontSize(Typography.body.size)
                            .color(Palette.textSecondary)
                            .styleModifier {
                                property("line-height", "1.7")
                                property("display", "block")
                            },
                    )
                }
            }
            Link(
                path = "/docs",
                modifier = Modifier
                    .classNames("sb-link-underline")
                    .color(Palette.accent)
                    .fontSize(Typography.body.size)
                    .fontWeight(500)
                    .styleModifier {
                        property("transition", "color ${Motion.DUR_BASE} ${Motion.EASE_STANDARD}")
                    },
            ) {
                SpanText("← Back to setup guide")
            }
        }
    }
}
